const {
  validateMobileNumber,
  validateEmail,
  validateDateOfBirth,
  validateExpirationDate,
  validateSSNNo,
} = require('../../../../lib/validate/validate');

const errRequired = 'Required';

export const hasError = (v) => {
  let keys = Object.keys(v);

  for (let index = 0; index < keys.length; index++) {
    const key = keys[index];

    if (typeof v[key] === 'object') {
      if (hasError(v[key])) {
        return true;
      }
    } else if (v[key]) {
      return true;
    }
  }

  return false;
};

export const validateAccount = (account, isBussinessAccount, companyAttachments) => {
  let v = {
    primaryOwner: {},
    jointOwner: {},
    corpPrimaryAgentAddress: {},
    corpOwnersList: [],
  };

  let totalOwnershipPercent = parseFloat(account.primaryOwner.ownershipPercent);

  if (['J', 'C', 'IRA'].includes(account.accountType) && !account.subType) {
    v.subType = errRequired;
  }

  if (account.accountType === 'C') {
    if (!account.tin) {
      v.tin = errRequired;
    }
    if (!account.accountName) {
      v.accountName = errRequired;
    }
    if (!account.formationCountry) {
      v.formationCountry = errRequired;
    }

    // Corporation Primary Owner Address (address type "Registered Agent Address")
    v.corpPrimaryAgentAddress = validateAddress(account.corpPrimaryAgentAddress);

    // Corporation Secondary Owners
    for (let index = 0; index < account.corpOwnersList.length; index++) {
      const coOwner = account.corpOwnersList[index];
      if (coOwner.isOwner) {
        totalOwnershipPercent =
          totalOwnershipPercent + parseFloat(coOwner.ownershipPercent);
      }

      v.corpOwnersList[index] = validateOwner(
        coOwner,
        'Secondary',
        isBussinessAccount,
        account.accountType
      );
    }

    for (let i = 0; i < companyAttachments?.length; i++) {
      const attachment = companyAttachments[i];
      if (!attachment.fileName) {
        v['attachment' + i] = `${attachment.requiredAttachment} attachment is Required`;
      }
    }
  }

  v.primaryOwner = validateOwner(
    account.primaryOwner,
    'Primary',
    isBussinessAccount,
    account.accountType
  );

  if (account.accountType === 'J') {
    v.jointOwner = validateOwner(
      account.jointOwner,
      'Secondary',
      isBussinessAccount,
      account.accountType
    );
  }

  const errTotalOwnership = 'Total owner percentage cannot exceed 100%';

  if (totalOwnershipPercent > 100) {
    v.primaryOwner.ownershipPercent = errTotalOwnership;
    for (let index = 0; index < account.corpOwnersList.length; index++) {
      const coOwner = account.corpOwnersList[index];
      if (coOwner.isOwner && parseFloat(coOwner.ownershipPercent)) {
        v.corpOwnersList[index].ownershipPercent = errTotalOwnership;
      }
    }
  }
  return v;
};

export const validateOwner = (owner, ownerType, isBussinessAccount, accountType) => {
  const v = {};

  if ((isBussinessAccount && ownerType === 'Primary') || ownerType !== 'Primary') {
    const e = validateMobileNumber(owner.phone || '');
    v.phone = e.msg;
  }

  if (ownerType !== 'Primary' || accountType === 'C') {
    if (!owner.email) {
      v.email = errRequired;
    } else if (validateEmail(owner.email)) {
      v.email = 'Invalid email address';
    }
    if (!owner.firstName) {
      v.firstName = errRequired;
    }
    if (!owner.lastName) {
      v.lastName = errRequired;
    }
  }

  if (accountType === 'C') {
    if (!owner.isOfficer && !owner.isOwner) {
      v.primaryType = errRequired;
    }

    if (owner.isOwner && !owner.ownershipPercent) {
      v.ownershipPercent = errRequired;
    }
  }

  if (ownerType === 'Primary' && accountType === 'C') {
    if (!owner.secondaryPhone) {
      v.secondaryPhone = errRequired;
    }
  }

  return { ...v, ...validateAddress(owner) };
};

export const validateAddress = (address) => {
  const v = {};

  if (!address.address1) {
    v.address1 = errRequired;
  }

  if (!address.city) {
    v.city = errRequired;
  }

  if (!address.country) {
    v.country = errRequired;
  }

  if (address.country === 'US' && !address.state) {
    v.state = errRequired;
  }

  if (!address.zip && ['US', 'CA'].includes(address.country)) {
    v.zip = errRequired;
  }

  return v;
};

export const validateIdentification = (id, owner) => {
  let v = {};

  if (!id.ownerId) {
    v.ownerId = "Please complete details of the owner in the 'Account' form.";
  }

  if (!id.imgGovernmentId) {
    v.imgGovernmentId = errRequired;
  }

  if (!id.identificationType) {
    v.identificationType = errRequired;
  }

  if (!id.identificationNo) {
    v.identificationNo = errRequired;
  }

  if (!id.issueDate) {
    v.issueDate = errRequired;
  }

  if (!id.expirationDate) {
    v.expirationDate = errRequired;
  }

  if (!id.countryOfIssuance) {
    v.countryOfIssuance = errRequired;
  }

  if (!id.dateOfBirth) {
    v.dateOfBirth = errRequired;
  } else if (!validateDateOfBirth(id.dateOfBirth)) {
    v.dateOfBirth = 'Age should be atleast 18 yrs old.';
  }

  if (!id.taxCountry) {
    v.taxCountry = errRequired;
  }

  if (!id.citizenship) {
    v.citizenship = errRequired;
  }

  if (id.isSameTaxCountry && !owner.country) {
    v.isSameTaxCountry = "Please complete 'Account' form.";
  }

  if (id.isSameCountryOfIssuance && !owner.country) {
    v.isSameCountryOfIssuance = "Please complete 'Account' form.";
  }

  if (id.isSameCitizenship && !owner.country) {
    v.isSameCitizenship = "Please complete 'Account' form.";
  }

  v.expirationDate = validateExpirationDate(id.expirationDate).msg;
  if (id.taxCountry === 'US') {
    const x = validateSSNNo(id.taxNo);
    v.taxNo = x.msg;
    v.taxNoTooltip = x.toolTip;
  }

  return v;
};

export const validateTrading = (trading) => {
  let v = {};
  if (!trading.marginType) {
    v.marginType = errRequired;
  }

  if (trading.marginType === 'Margin' && !trading.marginAgreement) {
    v.marginAgreement = errRequired;
  }

  if (trading.marginType === 'Margin' && !trading.marginDisclosure) {
    v.marginDisclosure = errRequired;
  }

  if (!trading.stocks && !trading.option) {
    v.tradingPrivileges = errRequired;
  }

  if (!trading.privacyConsent) {
    v.privacyConsent = errRequired;
  }

  return v;
};

export const validateDisclosures = (account, dynamicDisclosures) => {
  let v = {};

  if (!account.customerAccountAgreement) {
    v.customerAccountAgreement = errRequired;
  }

  if (account.marginType === 'Margin' && !account.marginAgreement) {
    v.marginAgreement = errRequired;
  }

  if (account.marginType === 'Margin' && !account.marginDisclosure) {
    v.marginDisclosure = errRequired;
  }

  for (let i = 0; i < dynamicDisclosures?.length; i++) {
    const disclosure = dynamicDisclosures[i];
    if (!disclosure.checked) {
      v['dynamic' + i] = 'Required';
    }
  }

  return v;
};

export const validateCustomerAccountAgreement = (agreement) => {
  let v = {};

  if (!agreement.customerAccountAgreement) {
    v.customerAccountAgreement = errRequired;
  }

  return v;
};

export const validateTrustedContact = (data) => {
  const v = {};

  if (!data.haveTrustedContact) {
    v.haveTrustedContact = errRequired;
    return v;
  }

  if (data.haveTrustedContact === 'No') {
    return v;
  }

  if (!data.firstName) {
    v.firstName = errRequired;
  }

  if (!data.lastName) {
    v.lastName = errRequired;
  }

  if (!data.dateOfBirth) {
    v.dateOfBirth = errRequired;
  }

  if (!data.relationship) {
    v.relationship = errRequired;
  }

  if (data.relationship === 'Other' && !data.other) {
    v.other = errRequired;
  }

  if (!data.phoneType) {
    v.phoneType = errRequired;
  }

  v.phone = validateMobileNumber(data.phone).msg;

  if (!data.emailType) {
    v.emailType = errRequired;
  }

  if (!data.email) {
    v.email = errRequired;
  } else if (validateEmail(data.email)) {
    v.email = 'Invalid email address';
  }

  if (!data.identificationType) {
    v.identificationType = errRequired;
  }

  if (!data.identificationValue) {
    v.identificationValue = errRequired;
  }

  if (data.identificationType === 'SSN') {
    const x = validateSSNNo(data.identificationValue);
    v.identificationValue = x.msg;
  }

  if (!data.addressType) {
    v.addressType = errRequired;
  }

  if (!data.address1) {
    v.address1 = errRequired;
  }

  if (!data.city) {
    v.city = errRequired;
  }

  if (!data.country) {
    v.country = errRequired;
  }

  if (!data.addressState && data.country === 'US') {
    v.addressState = errRequired;
  }

  if (!data.zip && ['US', 'CA'].includes(data.country)) {
    v.zip = errRequired;
  }
  return v;
};

export const validateEmployment = (data) => {
  const v = {};

  if (!data.employmentStatus) {
    v.employmentStatus = errRequired;
  }

  if (data.employmentStatus === 'Employed') {
    if (!data.employmentName) {
      v.employmentName = errRequired;
    }

    if (!data.employmentOccupation) {
      v.employmentOccupation = errRequired;
    }
  }

  if (data.employmentStatus === 'Self-Employed') {
    if (!data.natureOfBusiness) {
      v.natureOfBusiness = errRequired;
    }

    if (!data.employmentOccupation) {
      v.employmentOccupation = errRequired;
    }
  }

  if (data.employmentStatus === 'Unemployed') {
    if (!data.previousEmploymentName) {
      v.previousEmploymentName = errRequired;
    }

    if (!data.employmentOccupation) {
      v.employmentOccupation = errRequired;
    }
  }

  return v;
};

export const validateAssociation = (data, attachments) => {
  const v = {};

  if (data.ownership === 'Yes' && !data.stockOwnership) {
    v.stockOwnership = errRequired;
  }

  if (data.brokerMember === 'Yes') {
    if (!data.brokerName) {
      v.brokerName = errRequired;
    }
    if (!data.affiliatePerson) {
      v.affiliatePerson = errRequired;
    }

    if (!data.affiliatePersonPosition) {
      v.affiliatePersonPosition = errRequired;
    }

    if (!data.attachedFilesList.length && !attachments?.length) {
      v.attachments = 'required';
    }
  }
  return v;
};

export const validateObjective = (data, investmentObjectives) => {
  const v = {};

  if (!data.investmentObjectiveCode) {
    v.investmentObjectiveCode = errRequired;
  }
  if (!data.annualIncome) {
    v.annualIncome = errRequired;
  }
  if (!data.liquidNetWorth) {
    v.liquidNetWorth = errRequired;
  }

  if (!data.sourceOfFunds) {
    v.sourceOfFunds = errRequired;
  } else if (data.sourceOfFunds.includes('Others') && !data.sourceOfFundsDescription) {
    v.sourceOfFundsDescription = errRequired;
  }

  if (!data.riskTolerance) {
    v.riskTolerance = errRequired;
  }

  if (data.riskTolerance && data.investmentObjectiveCode && investmentObjectives.length) {
    const objective = investmentObjectives.find(
      (x) => x.code === data.investmentObjectiveCode
    );

    let validRiskTolerance = objective.note.split(',').map((x) => {
      return x.trim();
    });

    if (
      validRiskTolerance.length &&
      validRiskTolerance[0] !== '' &&
      !validRiskTolerance.includes(data.riskTolerance)
    ) {
      v.riskTolerance =
        'Invalid Setup! For the selected investment objective, you can only choose (' +
        validRiskTolerance.join(', ') +
        ') risk tolerance.';
    }
  }

  return v;
};
